import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  gql,
} from '@apollo/client';
import { createAuthLink, AuthOptions } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { onError } from '@apollo/client/link/error';
import { getAuthSession } from './authSession';
import {
  isLoggedInVar,
  isHideToDoVar,
  subInVar,
  selectedEntityIdInVar,
  recentClientsInVar,
} from '@admiin-com/ds-graphql';
import { typeDefs } from '@admiin-com/ds-graphql';

export const configureAppSyncClient = ({
  authType = 'AMAZON_COGNITO_USER_POOLS',
  graphQLAPIKey,
  graphQLAPIURL,
}: {
  authType?: 'AMAZON_COGNITO_USER_POOLS' | 'API_KEY' | 'IAM';
  graphQLAPIKey?: string;
  graphQLAPIURL: string;
}) => {
  const url = graphQLAPIURL;
  const region = 'us-east-1';

  let auth: AuthOptions;
  if (authType === 'API_KEY') {
    auth = { type: 'API_KEY', apiKey: graphQLAPIKey ?? '' };
  } else if (authType === 'IAM') {
    auth = {
      type: 'AWS_IAM',
      credentials: async () => (await getAuthSession()).credentials ?? null,
    };
  } else {
    auth = {
      type: 'AMAZON_COGNITO_USER_POOLS',
      jwtToken: async () =>
        (await getAuthSession()).tokens?.idToken?.toString() ?? '',
    };
  }

  const link = ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      graphQLErrors?.forEach(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
            locations
          )}, Path: ${path}`
        )
      );
      if (networkError) console.log(`[Network error]:`, networkError);
    }),
    createAuthLink({ url, region, auth }),
    createSubscriptionHandshakeLink(
      { url, region, auth },
      new HttpLink({ uri: graphQLAPIURL })
    ),
  ]);

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            isLoggedIn: { read: () => isLoggedInVar() },
            hideToDo: { read: () => isHideToDoVar() },
            sub: { read: () => subInVar() },
            recentClients: { read: () => recentClientsInVar() },
            selectedEntityId: { read: () => selectedEntityIdInVar() },

            entityUsersByUser: { keyArgs: ['searchName', 'role'] },
            // tasksByFirmId: {
            //   keyArgs: [
            //     'status',
            //     'type',
            //     'searchName',
            //     'createdBy',
            //     'firmId',
            //     'dueAtStart',
            //     'dueAtEnd',
            //     'entityId',
            //     'sortDirection',
            //   ],
            // },
            searchTasks: {
              keyArgs: [
                'status',
                'type',
                'searchName',
                'createdBy',
                'firmId',
                'dueAtStart',
                'dueAtEnd',
                'entityId',
                'sortDirection',
                'sortBy',
              ],
            },
            // new policy for searchTaskCounts
            searchTaskCounts: {
              keyArgs: [
                'status',
                'type',
                'searchName',
                'createdBy',
                'firmId',
                'dueAtStart',
                'dueAtEnd',
                'entityId',
                'sortDirection',
              ],
            },
            entityUsersByFirmId: {
              keyArgs: [
                'firmId',
                'searchName',
                'clientGroupCode',
                'sortDirection',
                'roles',
                'limit',
              ],
            },
            // new policy for tasksOverdueCounts
            tasksOverdueCounts: {
              keyArgs: ['firmId', 'entityId'],
            },

            listUserConversations: { merge: true },
            messagesByConversation: { merge: true },
          },
        },
        User: {
          fields: {
            profileImg: { merge: true },
          },
        },
        Entity: {
          fields: {
            logo: { merge: true },
            paymentMethods: {
              merge: true,
            },
            contact: {
              merge: true,
            },
            address: {
              merge: true,
            },
          },
        },
        Contact: {
          fields: {
            bank: {
              merge: true,
            },
          },
        },
      },
    }),
    connectToDevTools: true,
    typeDefs: gql(typeDefs),
  });

  return client;
};
